import { memo } from 'react'
import { Monitor, Smartphone, Tablet, Bot, HelpCircle } from 'lucide-react'
import { useTranslation } from 'react-i18next'

/**
 * 기기 분포.
 *
 * 원형 그래프 대신 가로 막대와 줄 목록을 쓴다. 비율을 견주는 데는 길이가
 * 각도보다 읽기 쉽고, 기기가 둘뿐일 때 원은 과한 표현이다.
 */

export interface DeviceStat {
  device: string
  clicks: number
  percentage: number
  color: string
}

interface DeviceBreakdownProps {
  stats: DeviceStat[]
}

/** 기기 이름에 맞는 그림. 모르는 값은 물음표로 둔다 */
function iconFor(device: string) {
  const key = device.toLowerCase()
  if (key.includes('desktop')) {
    return Monitor
  }
  if (key.includes('mobile') || key.includes('phone')) {
    return Smartphone
  }
  if (key.includes('tablet')) {
    return Tablet
  }
  if (key.includes('bot')) {
    return Bot
  }
  return HelpCircle
}

function DeviceBreakdown({ stats }: DeviceBreakdownProps) {
  const { t } = useTranslation()

  return (
    <section className="flex flex-col gap-4 rounded-2xl border border-gray-200 bg-white px-6 pb-6 pt-5 dark:border-gray-700 dark:bg-gray-800">
      <h3 className="text-[15px] font-bold text-gray-900 dark:text-white">
        {t('analyticsPage.deviceDistribution')}
      </h3>

      {stats.length === 0 ? (
        <p className="py-6 text-sm text-gray-500 dark:text-gray-400">
          {t('analyticsPage.noClicks')}
        </p>
      ) : (
        <>
          <div className="flex h-3 gap-[3px] overflow-hidden rounded-full">
            {stats.map(stat => (
              <span
                key={stat.device}
                className="h-full"
                style={{
                  width: `${stat.percentage}%`,
                  backgroundColor: stat.color,
                }}
              />
            ))}
          </div>

          <ul className="flex flex-col gap-3 text-sm">
            {stats.map(stat => {
              const Icon = iconFor(stat.device)
              return (
                <li key={stat.device} className="flex items-center gap-2.5">
                  <span
                    className="grid h-8 w-8 shrink-0 place-items-center rounded-[9px]"
                    style={{
                      backgroundColor: `${stat.color}1a`,
                      color: stat.color,
                    }}
                  >
                    <Icon className="h-[17px] w-[17px]" />
                  </span>
                  <span className="min-w-0 flex-1 truncate capitalize text-gray-900 dark:text-gray-200">
                    {stat.device}
                  </span>
                  <span className="tabular-nums text-gray-500 dark:text-gray-400">
                    {stat.clicks.toLocaleString()}
                  </span>
                  <span className="w-14 text-right font-bold tabular-nums text-gray-900 dark:text-white">
                    {stat.percentage}%
                  </span>
                </li>
              )
            })}
          </ul>
        </>
      )}
    </section>
  )
}

export default memo(DeviceBreakdown)
